'use client';

import { useTransition } from 'react';

import { isRedirectError } from 'next/dist/client/components/redirect-error';

import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { validateSignupFieldEntries } from '@kit/auth/signup-custom-fields';
import { Button } from '@kit/ui/button';
import { Form, FormField } from '@kit/ui/form';
import { Trans } from '@kit/ui/trans';

import {
  type ProfileSetupData,
  ProfileSetupSchema,
  type SignupCustomField,
  type SignupFamilyMember,
} from '../_lib/schema/profile-setup.schema';
import { CustomFieldInput } from '~/components/custom-fields/custom-field-input';

import { saveProfileSetupAction } from '../_lib/server/profile-setup-actions';

export interface ProfileSetupFormProps {
  parentFields: SignupCustomField[];
  childFields: SignupCustomField[];
  familyMembers: SignupFamilyMember[];
  defaultValues: ProfileSetupData;
  onComplete?: () => void;
}

export function ProfileSetupForm({
  parentFields,
  childFields,
  familyMembers,
  defaultValues,
  onComplete,
}: ProfileSetupFormProps) {
  const [isPending, startTransition] = useTransition();

  const form = useForm<ProfileSetupData>({
    resolver: zodResolver(ProfileSetupSchema),
    defaultValues: {
      ...defaultValues,
      parentFields: defaultValues.parentFields.map((entry) => ({
        ...entry,
        value:
          parentFields.find((field) => field.id === entry.fieldId)
            ?.field_type === 'boolean' && !('boolean' in entry.value)
            ? { boolean: false }
            : entry.value,
      })),
      childFieldGroups: defaultValues.childFieldGroups.map((group) => ({
        ...group,
        fields: group.fields.map((entry) => ({
          ...entry,
          value:
            childFields.find((field) => field.id === entry.fieldId)
              ?.field_type === 'boolean' && !('boolean' in entry.value)
              ? { boolean: false }
              : entry.value,
        })),
      })),
    },
  });

  const onSubmit = (data: ProfileSetupData) => {
    const errors = [
      ...validateSignupFieldEntries(parentFields, data.parentFields, 'parent'),
      ...data.childFieldGroups.flatMap((group) =>
        validateSignupFieldEntries(childFields, group.fields, 'child'),
      ),
    ];
    if (errors.length) {
      form.setError('root', { message: errors[0]!.message });
      return;
    }
    startTransition(async () => {
      try {
        const result = await saveProfileSetupAction({
          ...data,
          ...(onComplete ? { redirectAfterSave: false } : {}),
        });
        if (result?.success) onComplete?.();
      } catch (error) {
        // redirect() internally throws — let it propagate so Next.js handles it
        if (isRedirectError(error)) throw error;
        toast.error('Failed to save. Please try again.');
      }
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Parent fields */}
        {parentFields.length > 0 && (
          <div className="space-y-4">
            {familyMembers.length > 0 && (
              <h3 className="text-sm font-medium">
                <Trans i18nKey="auth:yourDetails" defaults="Your Details" />
              </h3>
            )}

            {parentFields.map((field, index) => (
              <FormField
                key={field.id}
                control={form.control}
                name={`parentFields.${index}.value`}
                render={({ field: input }) => (
                  <CustomFieldInput
                    field={field}
                    value={input.value}
                    onValueChange={input.onChange}
                    disabled={isPending}
                  />
                )}
              />
            ))}
          </div>
        )}

        {/* Per-child fields */}
        {childFields.length > 0 && familyMembers.length > 0 && (
          <div className="space-y-6">
            {familyMembers.map((child, childIdx) => (
              <div key={child.child_account_id} className="space-y-4">
                <h3 className="text-sm font-medium">{child.child_name}</h3>

                {childFields.map((field, fieldIdx) => (
                  <FormField
                    key={field.id}
                    control={form.control}
                    name={`childFieldGroups.${childIdx}.fields.${fieldIdx}.value`}
                    render={({ field: input }) => (
                      <CustomFieldInput
                        field={field}
                        value={input.value}
                        onValueChange={input.onChange}
                        disabled={isPending}
                      />
                    )}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {form.formState.errors.root && (
          <p role="alert" className="text-destructive text-sm">
            {form.formState.errors.root.message}
          </p>
        )}
        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            data-test="profile-setup-submit"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans i18nKey="common:saving" defaults="Saving..." />
              </>
            ) : (
              <Trans
                i18nKey="auth:completeProfile"
                defaults="Complete Profile"
              />
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
